import { useId, useRef, useState } from 'react';
import { AddDepartmentDialog } from './AddDepartmentDialog';
import { ApiError } from '../../identity/types';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { peopleGateway } from '../index';
import type { DepartmentOption, JobTitleOption } from '../types';

export interface CreateRoleDialogProps {
  departments: DepartmentOption[];
  /** The Department already chosen on the Add Employee form. When set it is fixed here, so the new role always fits that form. */
  departmentId: string;
  initialTitle: string;
  onClose: () => void;
  /** Called with the new ACTIVE role; the Add Employee form selects it and keeps every other field exactly as it was. */
  onCreated: (role: JobTitleOption) => void;
  /** Called when a department is added from here, so the form underneath can list it too. */
  onDepartmentAdded?: (department: DepartmentOption) => void;
}

const ADD_DEPARTMENT = '__add_department__';

/**
 * The small "Create new role" dialog opened from Add Employee. It creates an ACTIVE role with just a title and a department (the full
 * job description can be added later under People, Roles). It sits on top of Add Employee, which stays mounted underneath, so nothing
 * the person already typed is lost. Creating a role never sets pay and never changes what anyone can do in EPN. The server decides
 * who may create roles; a refusal is shown here.
 */
export function CreateRoleDialog({ departments, departmentId, initialTitle, onClose, onCreated, onDepartmentAdded }: CreateRoleDialogProps) {
  const uid = useId();
  const [title, setTitle] = useState(initialTitle);
  const [department, setDepartment] = useState(departmentId);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [deptError, setDeptError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [addDepartmentOpen, setAddDepartmentOpen] = useState(false);
  const [added, setAdded] = useState<DepartmentOption[]>([]);
  const allDepartments = [...departments, ...added.filter((a) => !departments.some((d) => d.id === a.id))];
  const titleRef = useRef<HTMLInputElement>(null);
  const deptRef = useRef<HTMLSelectElement>(null);

  async function submit() {
    const cleanTitle = title.split(/\s+/).filter(Boolean).join(' ');
    let failed = false;
    if (!cleanTitle) { setTitleError('Enter a role title.'); failed = true; } else setTitleError(null);
    if (!department) { setDeptError('Choose a department.'); failed = true; } else setDeptError(null);
    if (failed) {
      (!cleanTitle ? titleRef : deptRef).current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await peopleGateway.createJobTitle(cleanTitle, department);
      onCreated(created);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'You do not have permission to create roles. Ask a company administrator to create it, or choose an existing role.'
          : friendlyErrorMessage(err, err instanceof Error && !(err instanceof ApiError) ? err.message : 'Could not create this role.'),
      );
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title="Create new role"
      size="sm"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => void submit()} disabled={busy}>{busy ? 'Creating…' : 'Create role'}</button>
        </>
      )}
    >
      <p className="muted">
        This adds an active job role with a title and a department, and selects it for this employee. Everything you have entered so far is kept.
        You can add the full job description later under People, Roles. A job role does not set pay or system access.
      </p>
      <div className="jr-field">
        <label htmlFor={`${uid}-title`}>Role title <span aria-hidden="true" className="jr-req">*</span></label>
        <input
          ref={titleRef} id={`${uid}-title`} data-autofocus type="text" value={title} maxLength={150} autoComplete="off" aria-required="true"
          aria-invalid={titleError ? true : undefined} aria-describedby={titleError ? `${uid}-title-error` : undefined}
          onChange={(e) => { setTitle(e.target.value); setTitleError(null); }}
        />
        {titleError && <p id={`${uid}-title-error`} className="jr-error" role="alert">{titleError}</p>}
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-dept`}>Department <span aria-hidden="true" className="jr-req">*</span></label>
        <select
          ref={deptRef} id={`${uid}-dept`} value={department} aria-required="true" disabled={busy || departmentId !== ''}
          aria-invalid={deptError ? true : undefined} aria-describedby={deptError ? `${uid}-dept-error` : undefined}
          onChange={(e) => { if (e.target.value === ADD_DEPARTMENT) { setAddDepartmentOpen(true); return; } setDepartment(e.target.value); setDeptError(null); }}
        >
          <option value="">Select</option>
          {allDepartments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          {departmentId === '' && <option value={ADD_DEPARTMENT}>+ Add New Department</option>}
        </select>
        {departmentId !== '' && <p className="jr-hint">Matches the Department on the employee form.</p>}
        {deptError && <p id={`${uid}-dept-error`} className="jr-error" role="alert">{deptError}</p>}
      </div>
      {error && <p className="jr-error" role="alert">{error}</p>}
      {addDepartmentOpen && (
        <AddDepartmentDialog
          existing={allDepartments}
          onClose={() => setAddDepartmentOpen(false)}
          onAdded={(d) => {
            setAdded((prev) => (prev.some((x) => x.id === d.id) ? prev : [...prev, d]));
            onDepartmentAdded?.(d);
            setDepartment(d.id);
            setDeptError(null);
            setAddDepartmentOpen(false);
          }}
        />
      )}
    </ModalDialog>
  );
}
